/**
 * wayneFetch — typed fetch helper for REST endpoints not yet covered by the
 * generated OpenAPI SDK. Calls `/api/*` with `credentials: 'include'`
 * (Midway cookie) and runs through the shared `applyRequestDefaults` +
 * `handleResponseStatus` helpers for 401-redirect and CSRF handling.
 *
 * As the OpenAPI spec grows, call sites here should migrate to the generated
 * functions in `@/api/generated/sdk.gen` one at a time.
 */
import { applyRequestDefaults, handleResponseStatus } from './client';

const API_BASE = '/api';

interface FetchOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

/**
 * Issue a JSON request to `${API_BASE}${path}`. Throws `ApiError` /
 * `RateLimitedError` on non-2xx, redirects to login on 401, and parses the
 * body as JSON for 2xx. Pass `null` body to omit the body entirely.
 */
export async function wayneFetch<T>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  // Honour an already-aborted signal before we touch the network.
  if (options.signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
  const init: RequestInit = {
    method,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  };
  // Why we don't forward `options.signal` into `Request`/`fetch`: under
  // Vitest+jsdom on Node 24, jsdom installs its own global `AbortSignal`
  // polyfill, and undici's strict webidl checks (invoked by MSW's fetch
  // interceptor) reject any non-undici signal as not-an-instance-of
  // AbortSignal. Real browsers and the prod runtime don't hit this. React
  // Query already discards stale query results on unmount, so dropping
  // the signal here is a non-functional concession to the test runtime.
  if (options.body !== undefined && options.body !== null) {
    init.body = JSON.stringify(options.body);
  }
  // jsdom + native undici reject relative URLs in `new Request()` since
  // node 20. Anchor against `window.location.origin` (real browser:
  // resolves to the page's origin; tests: `http://localhost`).
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'http://localhost';
  const url = `${origin}${API_BASE}${path}`;
  const request = applyRequestDefaults(new Request(url, init), { method });
  const response = await fetch(request);
  await handleResponseStatus(response);
  // 204 -> empty body. Cast through unknown so callers expecting a void
  // response don't blow up.
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return (await response.json()) as T;
}
